import React from "react";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Select from "react-select";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService.js";
import { colors } from "../../theme/colors.js";

const emptyForm = {
  prefix: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const ManufacturerUserAddAction = ({
  manufacturerData,
  canAddUser,
  onUserAdded,
  className,
  buttonStyle,
}) => {
  const [modalUserVisible, setModalUserVisible] = React.useState(false);
  const [formValues, setFormValues] = React.useState(emptyForm);
  const [submitting, setSubmitting] = React.useState(false);

  const hasManufacturer =
    Boolean(manufacturerData?.ID) && Boolean(manufacturerData?.ManufacturerName);

  if (!canAddUser || !hasManufacturer) {
    return null;
  }

  const resetForm = () => {
    setFormValues(emptyForm);
  };

  const handleModalCancel = () => {
    setModalUserVisible(false);
    resetForm();
  };

  const updateFormValue = (field, value) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  };

  const addManufacturerUser = async () => {
    const firstName = formValues.firstName.trim();
    const lastName = formValues.lastName.trim();
    const email = formValues.email.trim();
    const phone = formValues.phone.trim();

    if (!firstName || !lastName || !email || !phone) {
      Swal.fire({
        title: "Error!",
        text: "Please fill all the fields",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to add this user to this manufacturer?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) {
      return;
    }

    const postObj = {
      ManufacturerID: manufacturerData.ID,
      ManufacturerName: manufacturerData.ManufacturerName,
      Prefix: formValues.prefix.trim(),
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Phone: phone,
      Active: true,
      Type: "Manufacturer",
    };

    setSubmitting(true);
    try {
      const serv = new APIService();
      const response = await serv.addDataToBackend(postObj, "register");

      if (response?.status === 200 || response?.status === 201) {
        setModalUserVisible(false);
        resetForm();
        await Swal.fire({
          icon: "success",
          title: "User Added",
          text: "User has been added successfully",
        });
        onUserAdded?.();
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error while adding user",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Error while adding user",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={className}>
      <Button
        onClick={() => setModalUserVisible(true)}
        type="primary"
        icon={<PlusOutlined />}
        style={{
          background: colors["primaryColor"],
          marginBottom: "10px",
          marginTop: "10px",
          ...buttonStyle,
        }}
      >
        Add User
      </Button>

      <Modal
        title="Add New Manufacturer User"
        open={modalUserVisible}
        onCancel={handleModalCancel}
        centered={true}
        footer={null}
        style={{ width: "800px" }}
      >
        <div>
          <Form layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Manufacturer">
                  <Select
                    defaultValue={{
                      label: `${manufacturerData.ManufacturerName}`,
                      value: `${manufacturerData.ID}`,
                    }}
                    value={{
                      label: `${manufacturerData.ManufacturerName}`,
                      value: `${manufacturerData.ID}`,
                    }}
                    isDisabled={true}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item label="Prefix">
                  <Input
                    placeholder="Dr."
                    onChange={(e) => updateFormValue("prefix", e.target.value)}
                    value={formValues.prefix}
                  />
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#616161",
                    }}
                  >
                    Optional
                  </p>
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="First Name">
                  <Input
                    placeholder="Enter First Name"
                    onChange={(e) =>
                      updateFormValue("firstName", e.target.value)
                    }
                    value={formValues.firstName}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="Last Name">
                  <Input
                    placeholder="Enter Last Name"
                    onChange={(e) =>
                      updateFormValue("lastName", e.target.value)
                    }
                    value={formValues.lastName}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={16}
              style={{ marginTop: "-15px", marginBottom: "-10px" }}
            >
              <Col span={10}>
                <Form.Item label="Email">
                  <Input
                    placeholder="Enter Email"
                    onChange={(e) => updateFormValue("email", e.target.value)}
                    value={formValues.email}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="Phone">
                  <Input
                    placeholder="Enter Phone"
                    onChange={(e) => updateFormValue("phone", e.target.value)}
                    value={formValues.phone}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Button
              type="primary"
              style={{ background: "#27a907" }}
              onClick={addManufacturerUser}
              loading={submitting}
              disabled={submitting}
            >
              Add User and Activate
            </Button>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default ManufacturerUserAddAction;
